import { formatCurrency } from "helpers/currency";

/**
 * Quote package with pricing breakdown
 */
export type QuotePackage = {
  package: string;
  plan_name: string;
  plan_variant_id: string;
  external_plan_name: string;
  total: number;
  breakdown: {
    package_price: number;
    weight_fee: { total_fee: number; additional_fee: number };
    pacemaker_fee: { total_fee: number; additional_fee: number };
    fingerprint_fee: { total_fee: number; additional_fee: number };
    inurnment_fee: {
      total_fee: number;
      additional_fee: number;
      total_quantity: number;
      unit_price: number;
    };
    transport_fee: { total_fee: number; additional_fee: number };
    delivery_fee: { total_fee: number; additional_fee: number };
    death_certificates: { total_fee: number; total_quantity: number };
    storage?: { total_fee: number; total_quantity: number };
    additional_services: {
      variant_id?: string;
      name: string;
      label: string;
      amount: number;
      total_quantity?: number;
      total_fee?: number;
    }[];
    state_fees: { name: string; amount: number }[];
    county_fees: { name: string; amount: number }[];
    expedite_fee?: { total_fee: number };
    inclusion_credits?: { offering_id: string; name: string; amount: number }[];
  };
};

/**
 * Quote context - contextual information that varies by quote type
 */
export type QuoteContext = {
  is_v2_market?: boolean;
  decedent_first_name?: string;
  decedent_last_name?: string;
};

/**
 * Saved quote response from API
 */
export type SavedQuoteResponse = {
  quote_id: string;
  quote_type: "at_need" | "pre_need";
  customer_first_name?: string;
  quote_context?: QuoteContext;
  packages: QuotePackage[];
  selected_package?: string;
  market_id?: string;
  expires_at: string | null;
  sent_at?: string;
  dcd_location?: string;
};

/**
 * Available add-on offering from API
 */
export type AvailableAddOn = {
  variant_id: string;
  title: string;
  pricing: { amount: number };
};

/**
 * Plan inclusion highlight from pricing API
 */
export type PlanHighlight = {
  title: string;
  description: string | null;
  tooltip: string | null;
  emphasize: boolean;
};

/**
 * Pricing plan from API (includes inclusion_highlights)
 */
export type PricingPlan = {
  variant_id: string;
  title: string;
  price: string;
  external: {
    title: string;
    subtitle: string;
    tagline: string;
    inclusion_highlights: PlanHighlight[];
  };
};

/**
 * Expired quote error response
 */
export type ExpiredQuoteResponse = {
  error: string;
  error_code: "expired";
};

/**
 * API response wrapper for saved quote endpoint
 * The backend returns { success: true, data: SavedQuoteResponse }
 */
export type SavedQuoteAPIResponse = {
  success: true;
  data: SavedQuoteResponse;
};

/**
 * Base plan inclusion item
 */
export type PlanInclusion = {
  name: string;
  subtext?: string;
  tooltip?: string;
  emphasize?: boolean;
};

/**
 * Base inclusions that come with all plans
 * These are shown in "What's Included" sections
 */
export const BASE_PLAN_INCLUSIONS: PlanInclusion[] = [
  { name: "Honorable cremation", emphasize: true },
  { name: "Permits and filing documents", emphasize: true },
  {
    name: "Transportation of loved one",
    subtext: "(25 miles)",
    emphasize: true,
  },
  { name: "Dedicated care specialist" },
  { name: "Temporary urn", subtext: "(on request)" },
  { name: "Secure Refrigeration", subtext: "(3 Business Days)" },
  { name: "Cremation container", subtext: "(under 200 lbs)" },
  { name: "Online obituary listing" },
  {
    name: "After grief support",
    tooltip:
      "When your loved one enters our care, you'll receive access to After's Grief Support. We'll check in with you by text and email each month for a full year, offering support, compassion, and guidance to help you find peace on your journey.",
  },
];

/**
 * Get the preference package (the one highlighted as user's preference)
 *
 * Logic:
 * - V2 markets: Use selected_package (the personalized quote)
 * - V1 markets: Default to "crest" package as recommendation
 * - Fallback: First package if preferred not found
 */
export function getPreferencePackage(
  packages: QuotePackage[],
  isV2Market: boolean,
  selectedPackage?: string,
): QuotePackage | null {
  if (!packages || packages.length === 0) return null;

  // V2 markets: Use the selected package (personalized quote)
  if (isV2Market && selectedPackage) {
    const selected = packages.find((pkg) => pkg.package === selectedPackage);
    if (selected) return selected;
  }

  // V1 markets: Default to "crest"
  if (!isV2Market) {
    const crestPackage = packages.find((pkg) => pkg.package === "crest");
    if (crestPackage) return crestPackage;
  }

  // Fallback to first package
  return packages[0];
}

/**
 * Get other packages (excluding the preference package)
 */
export function getOtherPackages(
  packages: QuotePackage[],
  preferencePackage: QuotePackage | null,
): QuotePackage[] {
  if (!preferencePackage) return packages;
  return packages.filter((pkg) => pkg.package !== preferencePackage.package);
}

type AdjustmentLineItem = { key: string; label: string; displayValue: string };

/**
 * The price breakdown's last lines before the total: a rush cremation charge
 * and a credit for each declined inclusion. The package total already counts
 * both, so the breakdown only adds up with them listed. Either may be absent
 * from the breakdown, which then yields no line for it.
 */
export function adjustmentLineItems(
  breakdown: QuotePackage["breakdown"],
): AdjustmentLineItem[] {
  const items: AdjustmentLineItem[] = [];
  const rushFee = breakdown.expedite_fee?.total_fee ?? 0;
  if (rushFee > 0) {
    items.push({
      key: "rush-cremation",
      label: "Rush cremation",
      displayValue: formatCurrency(rushFee),
    });
  }
  for (const credit of breakdown.inclusion_credits ?? []) {
    if (credit.amount <= 0) continue;
    items.push({
      key: `credit-${credit.offering_id}`,
      label: `${credit.name} credit`,
      displayValue: formatCurrency(-credit.amount),
    });
  }
  return items;
}
